import type {CreateExtArgs, Extension, ExtensionAPI} from '@wix/document-manager-core'
import type {
    Panel,
    BlocksEntity,
    CompRef,
    Pointer,
    ScopeMetaDataTemplate,
    Builder,
    WidgetDescriptor
} from '@wix/document-services-types'
import {deepClone} from '@wix/wix-immutable-proxy'
import _ from 'lodash'
import {METADATA_TYPES, VIEW_MODES} from '../../constants/constants'
import {stripHashIfExists} from '../../utils/refArrayUtils'
import type {StructureMetaDataExtensionAPI} from '../structureMetaData'
import {
    findWidgetByPageId,
    getAllSerializedCustomDefinitions,
    getAllWidgets,
    getBlocksData,
    getContainingWidgetsMap,
    getData,
    getDescriptorPointerById,
    getEntityByPage,
    getRootAppWidgetByPage,
    getRootCompIdByPointer,
    isDashboardPage,
    isDefaultPage,
    isWidgetPage,
    setWidgetDescriptorData,
    WidgetData
} from './blocksDataModel'
import {getWidgetManifest} from './manifestUtils'
import type {BlocksAppDescriptor, WidgetInfo, WidgetDescriptorInfo, BlocksAppDependency} from './types'
import {createPublicDescriptor} from './widgetDescriptors'
import {getExtendedMetadataTemplate} from './blocksMetadata'
import {getSlotPlaceholdersData} from './slotPlaceholders'
import {getAllDashboards} from './dashboards'
import {getPanelData} from './panels'
import {getPresetData} from './presets'
import {
    BuilderManifestOptions,
    generateBuilderManifest,
    getBlocksBuilderManifests,
    BuilderManifestInfo
} from './builderManifest'
import type {HooksExtensionApi} from '../hooks/hooks'
import {METADATA_HOOKS, MetadataHookEventData} from '../componentsMetadata/metadataHooks'
import type {FeaturesExtensionAPI} from '../features/features'

export type WidgetDevCenterInfo = Pick<WidgetDescriptor, 'devCenterWidgetId' | 'devCenterEditorReactComponentId'>

export interface BlocksAPI {
    getEntityByPage(pageRef: Pointer): BlocksEntity
    getWidgetsMapForBuild(): Record<string, WidgetInfo>
    getBlocksData(): any
    getBlocksAppDescriptor(): BlocksAppDescriptor
    isDashboardPage(pageRef: Pointer): boolean
    isDefaultPage(pageRef: Pointer): boolean
    getWidgetMetaDataTemplate(options: {
        appDefinitionId: string
        widgetPageId: string
        includeComponentsData?: boolean
        includeComponentsConnections?: boolean
    }): ScopeMetaDataTemplate | undefined
    getBlocksBuilderManifests(): Record<string, BuilderManifestInfo>
    generateBuilderManifest(widgetPageId: string, options?: BuilderManifestOptions): Builder.Manifest
    getComponentNicknameContext(compRef: Pointer): Pointer | null | undefined
    getBlocksAppDependecies(): BlocksAppDependency[]
    setDevCenterWidgetInfo(widgetPageId: string, widgetDevCenterInfo: Partial<WidgetDevCenterInfo>): void
}

export type BlocksExtensionAPI = ExtensionAPI & {
    blocks: BlocksAPI
}

const createExtension = (): Extension => {
    const createExtensionAPI = (createExtArgs: CreateExtArgs): BlocksExtensionAPI => {
        const {hooks} = createExtArgs.extensionAPI as HooksExtensionApi
        const {features} = createExtArgs.extensionAPI as FeaturesExtensionAPI

        const convertPanelInfoToWidgetsMap = (panelInfo: Panel) => {
            return {
                name: panelInfo.name,
                pageId: stripHashIfExists(panelInfo.rootCompId),
                height: panelInfo.height,
                title: panelInfo.title,
                pageUrl: panelInfo.pageUrl
            }
        }

        const getWidgetsMapForBuild = () => {
            const allWidgets = getAllWidgets(createExtArgs)

            const containingWidgetsMap = _.mapValues(getContainingWidgetsMap(createExtArgs), (widgetIds: string[]) =>
                widgetIds.map(widgetId => {
                    const pointer = getDescriptorPointerById(createExtArgs, widgetId)
                    return getData(createExtArgs, pointer).devCenterWidgetId
                })
            )

            return _.reduce(
                allWidgets,
                (accumulator, widgetData) => {
                    const {presetsInfo, panels, ...widgetInfo} = getWidgetInfo(widgetData)
                    accumulator[widgetInfo.widgetId] = {
                        ...widgetInfo,
                        nestedWidgetsIds: containingWidgetsMap[widgetData.pointer.id] || [],
                        panelsInfo: panels.map(panelInfo => convertPanelInfoToWidgetsMap(panelInfo))
                    }

                    if (createExtArgs.coreConfig.experimentInstance.isOpen('dm_sendPresetInfoInBlocksBuild')) {
                        accumulator[widgetInfo.widgetId].presetsInfo = presetsInfo
                    }

                    return accumulator
                },
                {}
            )
        }

        const getWidgetMetaDataTemplate = ({
            appDefinitionId,
            widgetPageId,
            includeComponentsData = false,
            includeComponentsConnections = false
        }: {
            appDefinitionId: string
            widgetPageId: string
            includeComponentsData?: boolean
            includeComponentsConnections?: boolean
        }): ScopeMetaDataTemplate | undefined => {
            const {pointers, extensionAPI} = createExtArgs
            const pagePointer = pointers.structure.getPage(widgetPageId, VIEW_MODES.DESKTOP)
            const rootWidget = getRootAppWidgetByPage(createExtArgs, pagePointer)
            const widgetDescriptor = findWidgetByPageId(createExtArgs, widgetPageId)

            if (!rootWidget || !widgetDescriptor) {
                return
            }

            const metaDataTemplate = (
                extensionAPI as StructureMetaDataExtensionAPI
            ).structureMetaData.getMetaDataTemplate(rootWidget as CompRef)

            return getExtendedMetadataTemplate(
                createExtArgs,
                metaDataTemplate,
                appDefinitionId,
                widgetDescriptor,
                rootWidget,
                includeComponentsData,
                includeComponentsConnections
            )
        }

        function getWidgetInfo(widgetData: WidgetData): WidgetInfo {
            const {pointer, variations, name, presets, plugin, panels = []} = widgetData

            const widgetRootCompId = getRootCompIdByPointer(createExtArgs, widgetData.pointer)
            if (!widgetRootCompId) {
                throw new Error('No widgetRootCompId')
            }

            const variationRootCompIds = _.map(variations, variationId => {
                const variationPointer = createExtArgs.pointers.data.getDataItemFromMaster(
                    _.replace(variationId, '#', '')
                )
                const variationRootCompId = getRootCompIdByPointer(createExtArgs, variationPointer)
                if (!variationRootCompId) {
                    throw new Error('No widgetRootCompId')
                }
                return variationRootCompId
            })
            const manifestInfo = getWidgetManifest(createExtArgs, pointer)

            const widgetPublicDescriptor = createPublicDescriptor(createExtArgs, pointer)

            const widgetInfo: WidgetInfo = {
                widgetId: widgetRootCompId,
                variations: variationRootCompIds,
                manifestInfo,
                widgetPublicDescriptor,
                presetsInfo: (presets ?? []).map(presetId => getPresetData(createExtArgs, presetId)),
                name,
                plugin: deepClone(plugin),
                panels: panels.map(panelId => getPanelData(createExtArgs, panelId))
            }

            if (createExtArgs.coreConfig.experimentInstance.isOpen('dm_addSiteWidgetSlotExtensions')) {
                const slotPlaceholders = getSlotPlaceholdersData(createExtArgs, widgetRootCompId)

                if (slotPlaceholders.length > 0) {
                    widgetInfo.slotPlaceholders = slotPlaceholders
                }
            }

            return widgetInfo
        }

        function getVariationsData(variationIds: string[]): any[] {
            return variationIds.map(variationId => {
                const variationPointer = createExtArgs.pointers.data.getDataItemFromMaster(
                    _.replace(variationId, '#', '')
                )
                return getData(createExtArgs, variationPointer)
            })
        }

        function getWidgetDescriptorInfo(widgetData: WidgetData): WidgetDescriptorInfo {
            const widgetInfo = getWidgetInfo(widgetData)
            const {devCenterWidgetId, devCenterEditorReactComponentId, kind, defaultSize, widgetApi} = deepClone(
                getData(createExtArgs, widgetData.pointer)
            )

            const variationsData = getVariationsData(widgetInfo.variations)

            return {
                ...widgetInfo,
                variationsData,
                widgetApi,
                devCenterWidgetId,
                devCenterEditorReactComponentId,
                defaultSize,
                kind
            }
        }

        function getBlocksAppDescriptor(): BlocksAppDescriptor {
            const allWidgets = getAllWidgets(createExtArgs)

            const widgetsMap = allWidgets.reduce((accumulator, widgetData) => {
                const widgetDescriptorInfo = getWidgetDescriptorInfo(widgetData)
                accumulator[widgetDescriptorInfo.widgetId] = widgetDescriptorInfo
                return accumulator
            }, {})

            const dashboards = getAllDashboards(createExtArgs)

            const customDefinitions = getAllSerializedCustomDefinitions(createExtArgs)

            return {
                widgetsMap,
                dashboards,
                customDefinitions
            }
        }

        function getBlocksAppDependecies(): BlocksAppDependency[] {
            const appStudioData = getBlocksData(createExtArgs) || {}
            return appStudioData.dependencies ?? []
        }

        const isConfigurationPage = (pagePointer: Pointer) => {
            const intentData = features.component.get(pagePointer, 'intent')
            return !!intentData && intentData.intent === 'configurationPage'
        }

        const getComponentNicknameContext = (compPointer: Pointer): Pointer | undefined | null => {
            const {pointers} = createExtArgs
            if (compPointer) {
                const pagePointer = pointers.structure.isPage(compPointer)
                    ? compPointer
                    : pointers.structure.getPageOfComponent(compPointer)

                if (!pagePointer) {
                    return null
                }

                if (pointers.components.isMasterPage(pagePointer)) {
                    return null
                }

                if (pagePointer) {
                    return getRootAppWidgetByPage(createExtArgs, pagePointer)
                }
            }
            return null
        }

        const setDevCenterWidgetInfo = (widgetPageId: string, widgetDevCenterInfo: Partial<WidgetDevCenterInfo>) => {
            const widgetDescriptor = findWidgetByPageId(createExtArgs, widgetPageId)
            if (!widgetDescriptor) {
                return
            }
            setWidgetDescriptorData(createExtArgs, widgetDescriptor.pointer, {
                devCenterWidgetId: widgetDevCenterInfo.devCenterWidgetId,
                devCenterEditorReactComponentId: widgetDevCenterInfo.devCenterEditorReactComponentId
            })
        }

        hooks.registerHook(
            METADATA_HOOKS[METADATA_TYPES.MAXIMUM_CHILDREN_NUMBER].id,
            ({componentPointer}: MetadataHookEventData, originalMetadataValue?: number) => {
                if (!componentPointer) {
                    return originalMetadataValue
                }

                if (isConfigurationPage(componentPointer) || isWidgetPage(createExtArgs, componentPointer.id)) {
                    return 1
                }
                return originalMetadataValue
            },
            'mobile.core.components.Page'
        )

        return {
            blocks: {
                getEntityByPage: (pageRef: Pointer) => getEntityByPage(createExtArgs, pageRef),
                getBlocksData: () => getBlocksData(createExtArgs),
                isDashboardPage: (pageRef: Pointer) => isDashboardPage(createExtArgs, pageRef),
                isDefaultPage: (pageRef: Pointer) => isDefaultPage(createExtArgs, pageRef),
                getWidgetsMapForBuild,
                getBlocksAppDescriptor,
                getWidgetMetaDataTemplate,
                getBlocksBuilderManifests: () => getBlocksBuilderManifests(createExtArgs),
                generateBuilderManifest: (widgetPageId: string, options?: BuilderManifestOptions) =>
                    generateBuilderManifest(createExtArgs, widgetPageId, options),
                getComponentNicknameContext,
                getBlocksAppDependecies,
                setDevCenterWidgetInfo
            }
        }
    }

    return {
        name: 'blocks',
        createExtensionAPI,
        dependencies: new Set(['structure', 'dataModel', 'page', 'nicknames', 'features', 'actionsAndBehaviors'])
    }
}

export {createExtension}
